import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnInit,
  viewChild,
} from '@angular/core';
import { ChatComposerComponent } from '@app/chat/components/chat-composer/chat-composer.component';
import { ChatAutoScrollDirective } from '@app/chat/directives/chat-auto-scroll.directive';
import { ContactInfo } from '@app/core/types/common/contact.types';
import { OneOnOneChatMessageComponent } from '@app/one-on-one-chat/components/one-on-one-chat-message/one-on-one-chat-message.component';
import { OneOnOneChatMessage } from '@app/one-on-one-chat/model/one-on-one-chat.types';
import { OneOnOneChatSandbox } from '@app/one-on-one-chat/sandboxes/one-on-one-chat.sandbox';
import { LongDatePipe } from '@app/shared/pipes/long-date.pipe';
import { SharedModule } from '@app/shared/shared.module';
import { ButtonComponent } from '@app/shared/ui/button/button.component';
import { LoaderComponent } from '@app/shared/ui/loader/loader.component';
import { ModalHeaderComponent } from '@app/shared/ui/modal-header/modal-header.component';
import { InfiniteScrollCustomEvent, IonContent, IonicModule } from '@ionic/angular';
import { TranslocoPipe } from '@jsverse/transloco';

/* Room left above the "new messages" divider when the chat opens on it. */
const UNREAD_SEPARATOR_MARGIN = 16;

@Component({
  selector: 'c-one-on-one-chat-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonicModule,
    SharedModule,
    TranslocoPipe,
    LongDatePipe,
    ModalHeaderComponent,
    LoaderComponent,
    ButtonComponent,
    OneOnOneChatMessageComponent,
    ChatComposerComponent,
    ChatAutoScrollDirective,
  ],
  templateUrl: './one-on-one-chat-modal.component.html',
  styleUrl: './one-on-one-chat-modal.component.scss',
})
export class OneOnOneChatModalComponent implements OnInit {
  @Input() contact: ContactInfo | null = null;
  @Input({ required: true }) agentExternalId: string;

  private readonly sandbox = inject(OneOnOneChatSandbox);
  private readonly content = viewChild.required(IonContent);
  private readonly autoScroll = viewChild.required(ChatAutoScrollDirective);
  private readonly unreadSeparator = viewChild<ElementRef<HTMLElement>>('unreadSeparator');

  private openedAtUnread = false;

  protected readonly messages = this.sandbox.messages;
  protected readonly days = this.sandbox.days;
  protected readonly loading = this.sandbox.loading;
  protected readonly error = this.sandbox.error;
  protected readonly hasOlder = this.sandbox.hasOlder;
  protected readonly loadingOlder = this.sandbox.loadingOlder;
  protected readonly firstUnreadMessageId = this.sandbox.firstUnreadMessageId;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.sandbox.reset());

    afterRenderEffect(() => {
      const separator = this.unreadSeparator()?.nativeElement;
      if (!separator || this.openedAtUnread) return;

      this.openedAtUnread = true;
      this.autoScroll().release();
      requestAnimationFrame(() => requestAnimationFrame(() => void this.scrollTo(separator)));
    });
  }

  ngOnInit(): void {
    this.sandbox.load(this.agentExternalId);
  }

  protected onRetryLoad(): void {
    this.sandbox.load(this.agentExternalId);
  }

  protected onSend(text: string): void {
    this.sandbox.send(text);
    this.autoScroll().follow();
  }

  protected onRetrySend(message: OneOnOneChatMessage): void {
    this.sandbox.retry(message);
  }

  protected onLoadOlder(event: InfiniteScrollCustomEvent): void {
    this.sandbox.loadOlder(() => event.target.complete());
  }

  private async scrollTo(element: HTMLElement): Promise<void> {
    const scrollElement = await this.content().getScrollElement();
    const offset =
      element.getBoundingClientRect().top - scrollElement.getBoundingClientRect().top + scrollElement.scrollTop;
    await this.content().scrollToPoint(0, offset - UNREAD_SEPARATOR_MARGIN, 0);
  }
}
